import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNotifications } from "../../../contexts/NotificationContext";
import { pinBackButton } from "../../../utils/pinBackButton";
import { openingsCountLabel } from "../../../utils/programName";

axios.defaults.withCredentials = true;
// Set default request timeout to 3 minutes (180000 ms) for search and other API calls
axios.defaults.timeout = 180000;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://108.175.2.100:8081";

const getCSRFToken = (name = "csrftoken") => {
  // console.log("Cookies:", document.cookie);

  // if (name === "sessionid") {
  //   return "q4lev5e7dug8qk6bxryfk4orpnmcmu66";
  // }

  // if (name === "csrftoken") {
  //   return "qOk0xrsbo8VCzPNCfsZGC1D4nACWvXaX";
  // }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const token = parts.pop()?.split(";").shift() || "";
    return token;
  }
  return "";
};

const ensureCSRFToken = async (): Promise<string> => {
  let token = getCSRFToken();
  if (token) {
    return token;
  }

  try {
    await fetch(`${API_BASE_URL}/`, {
      method: "GET",
      credentials: "include",
    });

    token = getCSRFToken();
    if (token) {
      return token;
    }
  } catch (error) {
    console.error("Failed to get CSRF token from server:", error);
  }

  throw new Error("Server-issued CSRF token is unavailable");
};

declare global {
  interface Window {
    google: {
      maps: {
        Map: any;
        Marker: any;
        InfoWindow: any;
        LatLng: any;
        LatLngBounds: any;
        MapTypeId: {
          ROADMAP: string;
        };
        Size: any;
        event: {
          addListener: (
            instance: any,
            eventName: string,
            handler: (...args: unknown[]) => unknown
          ) => any;
          removeListener: (listener: any) => void;
        };
      };
    };
  }
}

interface DaycareProgram {
  id: number;
  name: string;
  url?: string | null;
  available: number;
  is_free: boolean;
  is_nyc_program: boolean;
  price?: string | null; // Decimal as string with two digits after the decimal point
  pricing_period?: string | null;
}

interface DaycareRating {
  average: number | null;
  count: number;
}

interface Daycare {
  id: string; // uuid
  name: string;
  logo: string;
  address: string;
  latitude?: string;
  longitude?: string;
  /** Miles; API may send a number (location search) or string */
  distance: string | number | null;
  /** Child search sends "N min"; location-only search may send minutes as a number */
  walk_estimate: string | number | null;
  car_estimate: string | number | null;
  maps_url: string;
  programs: DaycareProgram[];
  rating: DaycareRating;
  ai_recommended: boolean;
  recommended?: boolean;
  show_available_openings: boolean;
  license_status?: string | null;
  programs_last_updated_at?: string | null;
}

interface SearchResponse {
  results: Daycare[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
}

interface ApplicationRequest {
  child_id: string;
  program_id: number;
  scheduled_at: string;
}

interface ApplicationResponse {
  status: string;
  message: string;
  application_id?: number;
}

interface Child {
  id: number;
  name: string;
  date_of_birth: string;
  gender: "m" | "f";
}

/** Avoid `value && <Node/>` when value can be 0 — React would render "0" as text. */
function hasPositiveEstimate(
  v: string | number | null | undefined
): boolean {
  if (v == null || v === "") return false;
  if (typeof v === "number") return !Number.isNaN(v) && v > 0;
  const s = String(v).trim();
  if (!s) return false;
  const n = parseFloat(s.replace(/min/gi, "").trim());
  if (!Number.isNaN(n)) return n > 0;
  return true;
}

function formatEstimateLabel(v: string | number): string {
  if (typeof v === "number") return `${v} min`;
  return String(v);
}

function formatAvailabilityLastUpdated(value?: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

const HomePageContent: React.FC = () => {
  const { state: notificationState } = useNotifications();
  const isAuthorized = notificationState.authChecked && !notificationState.unauthorized;
  const [selectedTab, setSelectedTab] = useState<string>("");
  const [scriptsLoaded, setScriptsLoaded] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<Daycare[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedChild, setSelectedChild] = useState<string>("");
  const [availabilityFilter, setAvailabilityFilter] = useState<string>("all");
  const [isSubmittingApplication, setIsSubmittingApplication] =
    useState<boolean>(false);
  const [availableChildren, setAvailableChildren] = useState<Child[]>([]);
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [mapMarkers, setMapMarkers] = useState<any[]>([]);
  const [selectedChildForApplication, setSelectedChildForApplication] =
    useState<string>("");
  const [childrenLoading, setChildrenLoading] = useState<boolean>(true);
  const [childrenLoadError, setChildrenLoadError] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [locationSearchMode, setLocationSearchMode] = useState<boolean>(false);
  /** Params for paginated location-only searches (URL bootstrap or provider map) */
  const [locationSearchCoords, setLocationSearchCoords] = useState<{
    lat: string;
    lon: string;
    radiusKm: number | null;
    nameQuery: string | null;
  } | null>(null);
  const [searchHasNext, setSearchHasNext] = useState<boolean>(false);
  const [searchListPage, setSearchListPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  /** When set, map marker for this daycare UUID uses a distinct "your location" style */
  const [ownDaycareUuid, setOwnDaycareUuid] = useState<string | null>(null);
  const availabilitySearchTimer = useRef<number | null>(null);
  const latestSearchRequest = useRef(0);
  const selectedChildRef = useRef("");
  // Add state for current application data
  const [currentApplicationData, setCurrentApplicationData] = useState<{
    daycare: Daycare | null;
    program: DaycareProgram | null;
  }>({ daycare: null, program: null });

  // Helper to format price (expects a decimal string) into `$X.YY` format
  const formatPrice = (priceStr: string) => {
    if (!priceStr) return "$0.00";
    const parsed = parseFloat(priceStr);
    if (isNaN(parsed)) return "$0.00";
    return `$${parsed.toFixed(2)}`;
  };

  const getProgramPriceLabel = (program: DaycareProgram) => {
    if (program.is_nyc_program || program.is_free) {
      return "Free";
    }
    if (!program.price || !program.pricing_period) {
      return "";
    }
    return `${formatPrice(program.price)}/${program.pricing_period}`;
  };

  // Check for cookies and log a warning if not set (no redirect)
  useEffect(() => {
    const checkCookies = () => {
      const csrfToken = getCSRFToken("csrftoken");

      if (!csrfToken) {
        console.warn("Cookies not set, some authenticated actions may fail.");
      }
    };

    // Check immediately
    checkCookies();

    // Also check after a short delay to ensure cookies are loaded
    const timeoutId = setTimeout(checkCookies, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  const searchDaycaresAPI = async (
    searchQuery: string | null,
    childId: string | null,
    filterAvailability: boolean,
    latitude?: string | null,
    longitude?: string | null,
    radiusKm?: number | null,
    page: number = 1
  ): Promise<SearchResponse> => {
    const params = new URLSearchParams({
      filter_availability: filterAvailability.toString(),
      page: String(page),
      ...(searchQuery ? { daycare_name: searchQuery } : {}),
      ...(childId ? { child_id: childId } : {}),
      ...(latitude ? { latitude } : {}),
      ...(longitude ? { longitude } : {}),
      ...(radiusKm != null ? { radius: String(radiusKm) } : {}),
    });
    const baseEndpoint = `${API_BASE_URL}/api/search/daycares/`;

    const queryString = params.toString();

    const fullUrl = `${baseEndpoint}?${queryString}`;

    const csrfToken = await ensureCSRFToken();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    };

    const response = await axios.get(fullUrl, {
      withCredentials: true,
      headers,
    });

    if (response.status !== 200) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.data;
  };

  // If the page was opened with latitude/longitude query params, perform a location-only search
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const lat = urlParams.get('latitude') || urlParams.get('lat');
      const lon = urlParams.get('longitude') || urlParams.get('lon');
      const q = urlParams.get('q') || urlParams.get('address') || null;
      if (lat && lon) {
        setLocationSearchMode(true);
        (async () => {
          setIsLoading(true);
          setHasSearched(true);
          try {
            const response = await searchDaycaresAPI(q, null, false, lat, lon, null, 1);
            setLocationSearchCoords({
              lat,
              lon,
              radiusKm: null,
              nameQuery: q,
            });
            console.debug('Location search returned', response?.results?.length, 'results');
            setSearchResults(response.results);
            setSearchHasNext(response.has_next ?? false);
            setSearchListPage(1);
          } catch (err) {
            console.error('Location search failed:', err);
            setSearchResults([]);
          } finally {
            setIsLoading(false);
          }
        })();
      }
    } catch {
      // ignore URL parsing errors
    }
  }, []);

  const getChildrenAPI = async () => {
    try {
      const csrfToken = await ensureCSRFToken();
      // Session cookie is typically HttpOnly; don't try to read it from JS.

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      };

      // if (sessionId) {
      //   headers["Cookie"] = `sessionid=${sessionId}; csrftoken=${csrfToken}`;
      // } else {
      //   console.error(
      //     "No sessionid found in cookies. Request may fail with 401."
      //   );
      // }

      const response = await axios.get(`${API_BASE_URL}/api/search/my-children/`, {
        withCredentials: true,
        headers,
      });

      if (response.status !== 200) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error in getChildrenAPI:", error);
      throw error;
    }
  };

  const submitApplicationAPI = async (
    request: ApplicationRequest
  ): Promise<ApplicationResponse> => {
    const endpoint = "/api/search/submit-application/";
    const csrfToken = await ensureCSRFToken();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    };

    try {
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, request, {
        withCredentials: true,
        headers,
      });

      if (response.status !== 200) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return response.data;
    } catch (error: any) {
      // Handle validation errors from the backend
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  };

  const generateStarRating = (average: number | null, count: number) => {
    const avgRating = average || 0;
    const fullStars = Math.floor(avgRating);
    const hasHalfStar = avgRating % 1 !== 0;
    const emptyStars = 5 - Math.ceil(avgRating);

    return (
      <>
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} className="fa fa-star"></span>
        ))}
        {hasHalfStar && <span className="fa fa-star-half-o"></span>}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} className="fa fa-star-o"></span>
        ))}
        {` (${count})`}
      </>
    );
  };

  const DaycareCard: React.FC<{ daycare: Daycare }> = ({ daycare }) => {
    const logoUrl = daycare.logo
      ? `${API_BASE_URL}${daycare.logo}`
      : "/src/assets/avatar-2.svg";
    const availabilityLastUpdated = formatAvailabilityLastUpdated(
      daycare.programs_last_updated_at
    );

    const handleApplicationClick = (program: DaycareProgram) => {
      const isAvailable = !program.is_nyc_program && program.available > 0;

      // Set current application data
      setCurrentApplicationData({ daycare, program });

      // Set selected child if available
      if (selectedChild) {
        setSelectedChildForApplication(selectedChild);
      }

      // Populate modal data based on availability
      setTimeout(() => {
        const modalTarget = isAvailable ? "#modalTabSend" : "#modalWaiting";
        const modalElement = document.querySelector(modalTarget);

        if (modalElement) {
          if (isAvailable) {
            // Populate Send Application modal
            const programElement = modalElement.querySelector("#modalProgramSend");
            const priceElement = modalElement.querySelector("#modalPriceSend");
            const openingsButton = modalElement.querySelector(".btn-modal-sent");

            if (programElement) {
              programElement.textContent = program.name;
            }
            if (priceElement) {
              priceElement.textContent = getProgramPriceLabel(program);
            }
            if (openingsButton) {
              openingsButton.textContent = daycare.show_available_openings 
                ? openingsCountLabel(program.available)
                : "Have openings";
              openingsButton.className = "btn btn-success btn-modal-sent rounded-0";
            }
          } else {
            // Populate Waiting List modal
            const programElement = modalElement.querySelector("#modalProgram");
            const priceElement = modalElement.querySelector("#modalPrice");
            const openingsButton = modalElement.querySelector("#modalOpeningsWaiting");

            if (programElement) {
              programElement.textContent = program.name;
            }
            if (priceElement) {
              priceElement.textContent = getProgramPriceLabel(program);
            }
            if (openingsButton) {
              openingsButton.textContent = daycare.show_available_openings 
                ? "Waiting List" 
                : "No openings";
              openingsButton.className = "btn btn-info rounded-0";
            }

            // NYC city programs (2-K/3-K/Pre-K) use the updates-list copy
            const waitingTitle = modalElement.querySelector("#modalWaitingTitle");
            const waitingSubmit = modalElement.querySelector("#sendApplicationBtnWaiting");
            if (waitingTitle) {
              waitingTitle.textContent = program.is_nyc_program
                ? "Join Day Care List For Updates"
                : "Join a waiting list";
            }
            if (waitingSubmit) {
              if (program.is_nyc_program) {
                waitingSubmit.innerHTML = "Join Day Care<br>List For Updates";
              } else {
                waitingSubmit.textContent = "Join a waiting list";
              }
            }
          }
        }
      }, 100);
    };

    return (
      <div className="card mb-3 w-100">
        <div className="card-body rounded p-2 position-relative">
          <div className="row">
            <div className="col-7">
              <div className="d-flex align-items-center">
                <img src={logoUrl} alt="Avatar" className="card-avatar me-2" />
                <div className="d-flex flex-column name-garden">
                  <a href={`${API_BASE_URL}/provider/public/${daycare.id}/`} target="_blank" rel="noopener noreferrer">
                    <small className="fw-bold">{daycare.name}</small>
                  </a>
                  <small
                    className="rating"
                    data-rating={daycare.rating.average || 0}
                  >
                    {generateStarRating(
                      daycare.rating.average,
                      daycare.rating.count
                    )}
                  </small>
                  {daycare.license_status && (
                    <small className="text-muted">
                      License: {daycare.license_status}
                    </small>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <small className="smaller text-muted pt-2">
                  <i className="bi bi-geo-alt-fill"></i>
                  {daycare.address}
                </small>
              </div>
              {daycare.distance != null &&
                daycare.distance !== "" &&
                !(typeof daycare.distance === "number" && Number.isNaN(daycare.distance)) && (
                <small className="d-block pt-2">
                  {typeof daycare.distance === "number"
                    ? daycare.distance.toFixed(2)
                    : daycare.distance}{" "}
                  miles
                </small>
              )}
              {(hasPositiveEstimate(daycare.walk_estimate) ||
                hasPositiveEstimate(daycare.car_estimate)) && (
                <small className="d-block pt-2">
                  <span className="text-muted">Estimated:</span>
                  {hasPositiveEstimate(daycare.walk_estimate) && (
                    <span>
                      <i className="bi bi-person-walking"></i>
                      {formatEstimateLabel(daycare.walk_estimate as string | number)},
                    </span>
                  )}
                  {hasPositiveEstimate(daycare.car_estimate) && (
                    <span>
                      <i className="bi bi-car-front"></i>
                      {formatEstimateLabel(daycare.car_estimate as string | number)}
                    </span>
                  )}
                </small>
              )}
              <small className="d-block text-muted pt-2">
                <span className="text-muted">Maps:</span>
                <a
                  href={daycare.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Google Maps
                </a>
              </small>
              {availabilityLastUpdated && (
                <small className="d-block text-muted pt-2">
                  Availability updated: {availabilityLastUpdated}
                </small>
              )}
            </div>
            <div className="col-5">
              <div
                className="d-flex flex-column ms-2 gap-2 recommended"
                style={{ alignItems: "end" }}
              >
                {daycare.recommended && (
                  <div
                    className="custom-badge d-inline-flex justify-content-center align-items-center"
                    data-bs-toggle="tooltip"
                    data-bs-original-title="This daycare is recommended by our team."
                  >
                    <i
                      className="bi bi-star-fill me-1"
                      style={{ fontSize: "0.8rem" }}
                    ></i>
                    <span>Recommended</span>
                  </div>
                )}
                {daycare.ai_recommended && (
                  <div
                    className="custom-badge d-inline-flex justify-content-center align-items-center"
                    data-bs-toggle="tooltip"
                    data-bs-original-title="Our AI algorithm recommends this daycare based on your preferences."
                    style={{ maxWidth: "122px", minWidth: "122px" }}
                  >
                    <i className="me-1" style={{ fontSize: "0.8rem" }}>
                      ✨
                    </i>
                    <span>AI recommends</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex flex-column align-items-start mt-3">
            {daycare.programs.map((program) => {
              const isNyc = program.is_nyc_program;
              const isAvailable = !isNyc && program.available > 0;
              const buttonClass = isAvailable
                ? "btn-success btn-modal-sent"
                : isNyc
                ? "btn-info btn-modal-waiting btn-two-line"
                : "btn-info btn-modal-waiting";

              const buttonText = isNyc ? (
                <>
                  Join Day Care
                  <br />
                  List For Updates
                </>
              ) : isAvailable
                ? daycare.show_available_openings
                  ? openingsCountLabel(program.available)
                  : "Have openings"
                : daycare.show_available_openings
                ? "Waiting List"
                : "No openings";

              const displayProgramName = program.name;

              return (
                <div key={program.id} className="map-program-row">
                  <div className="map-program-copy">
                    <small className="map-program-name smaller fw-semibold">
                      {program.url ? (
                        <a
                          href={program.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          {displayProgramName}
                        </a>
                      ) : (
                        displayProgramName
                      )}
                    </small>
                    <span className="map-program-price small fw-bold">
                      {getProgramPriceLabel(program)}
                    </span>
                  </div>
                  <button
                    className={`btn ${buttonClass} map-program-cta text-white px-2 rounded-0`}
                    type="button"
                    data-bs-toggle="modal"
                    data-bs-target={isAvailable ? "#modalTabSend" : "#modalWaiting"}
                    onClick={() => handleApplicationClick(program)}
                  >
                    {buttonText}
                    <i className="bi bi-arrow-right ms-2"></i>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const loadMoreSearchResults = async () => {
    if (!searchHasNext || isLoadingMore) return;
    const nextPage = searchListPage + 1;
    setIsLoadingMore(true);
    try {
      let filter = availabilityFilter === "all" ? false : true;
      if (childrenLoadError) filter = false;

      let response: SearchResponse;
      if (locationSearchMode && locationSearchCoords) {
        response = await searchDaycaresAPI(
          locationSearchCoords.nameQuery,
          null,
          false,
          locationSearchCoords.lat,
          locationSearchCoords.lon,
          locationSearchCoords.radiusKm,
          nextPage
        );
      } else {
        const childId = selectedChild;
        if (selectedChild === "" && !childrenLoadError) {
          return;
        }
        response = await searchDaycaresAPI(
          searchQuery,
          childId,
          filter,
          undefined,
          undefined,
          undefined,
          nextPage
        );
      }

      setSearchResults((prev) => [...prev, ...response.results]);
      setSearchListPage(nextPage);
      setSearchHasNext(response.has_next ?? false);
    } catch (error) {
      console.error("Load more failed:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const performSearch = async (availabilityOverride?: string) => {
    const inLocationMode = locationSearchMode && !!locationSearchCoords;
    // Geo mode without a child, or children-load-error, can search without child_id
    if (selectedChild === "" && !childrenLoadError && !inLocationMode) {
      console.warn("No child selected for search");
      return;
    }

    const childId = selectedChild;
    const requestId = ++latestSearchRequest.current;
    selectedChildRef.current = childId;

    const useLocationSearch = inLocationMode && !childId;
    if (childId) {
      setLocationSearchMode(false);
      setLocationSearchCoords(null);
    }
    setIsLoading(true);
    setHasSearched(true);
    let filter =
      (availabilityOverride ?? availabilityFilter) === "all" ? false : true;

    // When children couldn't be loaded, disable availability filtering
    if (childrenLoadError || useLocationSearch) {
      filter = false;
    }

    try {
      let response: SearchResponse;
      if (useLocationSearch && locationSearchCoords) {
        const coords = locationSearchCoords;
        response = await searchDaycaresAPI(
          searchQuery || coords.nameQuery,
          null,
          false,
          coords.lat,
          coords.lon,
          coords.radiusKm,
          1
        );
      } else {
        response = await searchDaycaresAPI(
          searchQuery,
          childId,
          filter,
          undefined,
          undefined,
          undefined,
          1
        );
      }
      if (
        requestId !== latestSearchRequest.current ||
        (!useLocationSearch && childId !== selectedChildRef.current)
      ) {
        return;
      }
      setHasSearched(true);
      setSearchResults(response.results);
      setSearchHasNext(response.has_next ?? false);
      setSearchListPage(1);
    } catch (error) {
      if (requestId === latestSearchRequest.current) {
        console.error("Search failed:", error);
        setSearchResults([]);
      }
    } finally {
      if (requestId === latestSearchRequest.current) {
        setIsLoading(false);
      }
    }
  };

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchIconClick = () => {
    performSearch();
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      performSearch();
    }
  };

  const handleChildSelection = (childId: string) => {
    if (availabilitySearchTimer.current !== null) {
      window.clearTimeout(availabilitySearchTimer.current);
      availabilitySearchTimer.current = null;
    }
    selectedChildRef.current = childId;
    latestSearchRequest.current += 1;
    setIsLoading(false);
    setSelectedChild(childId);
    setSelectedTab(childId);
  };

  const handleAvailabilityFilterChange = (value: string) => {
    setAvailabilityFilter(value);
    if (availabilitySearchTimer.current !== null) {
      window.clearTimeout(availabilitySearchTimer.current);
    }
    availabilitySearchTimer.current = window.setTimeout(() => {
      void performSearch(value);
      availabilitySearchTimer.current = null;
    }, 100);
  };

  const handleApplicationSubmit = async (status: "sent" | "waiting_list") => {
    setIsSubmittingApplication(true);

    try {
      const modalId = status === "sent" ? "#modalTabSend" : "#modalWaiting";
      const modalElement = document.querySelector(modalId);

      const selectedChild = availableChildren.find(
        (child) => child.id.toString() === selectedChildForApplication
      );

      if (!selectedChild) {
        throw new Error("Please select a child for the application");
      }

      if (!currentApplicationData.program) {
        throw new Error("No program selected for application");
      }

      const request: ApplicationRequest = {
        child_id: selectedChild.id.toString(),
        program_id: currentApplicationData.program.id,
        scheduled_at: new Date().toISOString(),
      };

      const response = await submitApplicationAPI(request);

      if (response.status === "sent" || response.status === "waiting_list") {
        const modal = document.getElementById(modalId.replace("#", ""));
        if (modal) {
          const modalInstance = (window as any).bootstrap.Modal.getInstance(
            modal
          );
          if (modalInstance) {
            modalInstance.hide();
          }
          setTimeout(() => {
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) {
              backdrop.remove();
            }

            document.body.classList.remove("modal-open");
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
          }, 300);
        }

        // Clear form data
        const textarea = modalElement?.querySelector("textarea") as HTMLTextAreaElement;
        if (textarea) {
          textarea.value = "";
        }
        setSelectedChildForApplication("");
        setCurrentApplicationData({ daycare: null, program: null });

        const toastElement = document.getElementById("toastMessage");
        const toastMessageText = document.getElementById("toastMessageText");
        if (toastElement && toastMessageText) {
          toastMessageText.textContent =
            response.message || "Application submitted successfully!";

          toastElement.style.opacity = "0";
          toastElement.style.transform = "translateX(100%)";
          toastElement.style.transition =
            "opacity 0.5s ease, transform 0.5s ease";
          toastElement.classList.add("show");

          setTimeout(() => {
            toastElement.style.opacity = "1";
            toastElement.style.transform = "translateX(0)";
          }, 10);

          setTimeout(() => {
            toastElement.style.opacity = "0";
            toastElement.style.transform = "translateX(100%)";
          }, 4000);

          setTimeout(() => {
            toastElement.classList.remove("show");
          }, 4500);
        }

        // Application submitted successfully
      } else {
        throw new Error(response.message || "Application submission failed");
      }
    } catch (error) {
      console.error("Application submission failed:", error);

      const toastElement = document.getElementById("toastMessageHeader");
      if (toastElement) {
        const toastBody = toastElement.querySelector(".toast-body span");
        if (toastBody) {
          toastBody.textContent = `${error instanceof Error ? error.message : "Unknown error"}`;
        }
        const toast = new (window as any).bootstrap.Toast(toastElement);
        toast.show();
      }
    } finally {
      setIsSubmittingApplication(false);
    }
  };

  const updateMapMarkers = (daycares: Daycare[], ownUuid: string | null) => {
    if (!mapInstance || !window.google || !window.google.maps) return;

    mapMarkers.forEach((marker) => marker.setMap(null));
    setMapMarkers([]);

    const newMarkers: any[] = [];

    daycares.forEach((daycare) => {
      if (!daycare.latitude || !daycare.longitude) return;

      const lat = parseFloat(daycare.latitude);
      const lng = parseFloat(daycare.longitude);

      if (isNaN(lat) || isNaN(lng)) return;

      const program = daycare.programs[0];
      const isAvailable = program && program.available > 0;
      const isOwn = Boolean(ownUuid && daycare.id === ownUuid);
      const availabilityLastUpdated = formatAvailabilityLastUpdated(
        daycare.programs_last_updated_at
      );

      const ownMarkerSvg = `
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" fill="#6f42c1" stroke="#fff" stroke-width="2"/>
                  <path d="M12 6v6M9 9h6" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                </svg>
              `;
      const iconUrl = isOwn
        ? "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(ownMarkerSvg)
        : isAvailable
            ? "data:image/svg+xml;charset=UTF-8," +
              encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#28a745" stroke="#fff" stroke-width="2"/>
                  <path d="M8 12l2 2 6-6" stroke="#fff" stroke-width="2" fill="none"/>
                </svg>
              `)
            : "data:image/svg+xml;charset=UTF-8," +
              encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#17a2b8" stroke="#fff" stroke-width="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="#fff" stroke-width="2"/>
                </svg>
              `);

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapInstance,
        title: isOwn ? `${daycare.name} (your daycare)` : daycare.name,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(isOwn ? 34 : 30, isOwn ? 34 : 30),
        },
        zIndex: isOwn ? 999 : undefined,
      });

      const infoWindowContent = document.createElement("div");
      infoWindowContent.style.cssText = "padding: 10px; max-width: 250px;";

      const name = document.createElement("h6");
      name.style.cssText = "margin: 0 0 8px 0; font-weight: bold;";
      name.textContent = daycare.name;
      infoWindowContent.appendChild(name);

      const rating = document.createElement("div");
      rating.style.cssText = "display: flex; align-items: center; margin-bottom: 8px;";
      const stars = document.createElement("div");
      stars.style.display = "flex";
      Array.from({ length: 5 }).forEach((_, i) => {
        const star = document.createElement("span");
        star.style.color =
          i < (daycare.rating.average || 0) ? "#ffc107" : "#e9ecef";
        star.textContent = "★";
        stars.appendChild(star);
      });
      rating.appendChild(stars);
      const ratingCount = document.createElement("span");
      ratingCount.style.cssText = "margin-left: 8px; color: #6c757d;";
      ratingCount.textContent = `(${daycare.rating.count})`;
      rating.appendChild(ratingCount);
      infoWindowContent.appendChild(rating);

      const address = document.createElement("p");
      address.style.cssText =
        "margin: 0 0 8px 0; font-size: 14px; color: #6c757d;";
      const addressIcon = document.createElement("i");
      addressIcon.className = "bi bi-geo-alt-fill";
      address.appendChild(addressIcon);
      address.appendChild(document.createTextNode(` ${daycare.address}`));
      infoWindowContent.appendChild(address);

      if (availabilityLastUpdated) {
        const updated = document.createElement("p");
        updated.style.cssText =
          "margin: 0 0 8px 0; font-size: 12px; color: #6c757d;";
        updated.textContent = `Availability updated: ${availabilityLastUpdated}`;
        infoWindowContent.appendChild(updated);
      }

      const availability = document.createElement("div");
      availability.style.cssText =
        "display: flex; justify-content: space-between; align-items: center;";
      const availabilityBadge = document.createElement("span");
      availabilityBadge.style.cssText = `padding: 4px 8px; border-radius: 4px; font-size: 12px; background-color: ${
        isAvailable ? "#d4edda" : "#d1ecf1"
      }; color: ${isAvailable ? "#155724" : "#0c5460"};`;
      availabilityBadge.textContent = isAvailable
        ? daycare.show_available_openings
          ? openingsCountLabel(program.available)
          : "Have openings"
        : daycare.show_available_openings
          ? "Waiting List"
          : "No openings";
      availability.appendChild(availabilityBadge);
      if (daycare.distance) {
        const distance = document.createElement("small");
        distance.style.color = "#6c757d";
        distance.textContent = String(daycare.distance);
        availability.appendChild(distance);
      }
      infoWindowContent.appendChild(availability);

      if (daycare.license_status) {
        const licenseLine = document.createElement("small");
        licenseLine.style.color = "#6c757d";
        licenseLine.textContent = `License: ${daycare.license_status}`;
        infoWindowContent.appendChild(licenseLine);
      }

      if (program) {
        const programDetails = document.createElement("div");
        programDetails.style.cssText =
          "margin-top: 8px; padding-top: 8px; border-top: 1px solid #e9ecef;";
        const programLabel = document.createElement("small");
        const programName = document.createElement("strong");
        const displayProgramName = program.name;
        if (program.url) {
          try {
            const programUrl = new URL(program.url, window.location.origin);
            if (programUrl.protocol === "http:" || programUrl.protocol === "https:") {
              const programLink = document.createElement("a");
              programLink.href = programUrl.href;
              programLink.target = "_blank";
              programLink.rel = "noopener noreferrer";
              programLink.textContent = displayProgramName;
              programName.appendChild(programLink);
            } else {
              programName.textContent = displayProgramName;
            }
          } catch {
            programName.textContent = displayProgramName;
          }
        } else {
          programName.textContent = displayProgramName;
        }
        programLabel.appendChild(programName);
        programDetails.appendChild(programLabel);
        programDetails.appendChild(document.createElement("br"));
        const programPrice = document.createElement("small");
        programPrice.textContent = getProgramPriceLabel(program);
        programDetails.appendChild(programPrice);
        infoWindowContent.appendChild(programDetails);
      }

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoWindowContent,
      });

      marker.addListener("click", () => {
        infoWindow.open(mapInstance, marker);
      });

      newMarkers.push(marker);
    });

    setMapMarkers(newMarkers);

    if (newMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach((marker) => bounds.extend(marker.getPosition()));
      mapInstance.fitBounds(bounds);

      if (newMarkers.length === 1) {
        const listener = window.google.maps.event.addListener(
          mapInstance,
          "idle",
          () => {
            if (mapInstance.getZoom() > 15) mapInstance.setZoom(15);
            window.google.maps.event.removeListener(listener);
          }
        );
      }
    }
  };

  const loadChildren = async () => {
    try {
      setChildrenLoading(true);
      setOwnDaycareUuid(null);

      const response = await getChildrenAPI();
      setAvailableChildren(response.children);
      setChildrenLoadError(false);

      if (response.children.length > 0) {
        const firstChildId = response.children[0].id.toString();
        setSelectedChild(firstChildId);
        setSelectedTab(firstChildId);
      } else {
        // If no children are available, set default values
        setSelectedChild("");
        setSelectedTab("");
      }
    } catch (error) {
      console.error("Failed to load children:", error);
      setAvailableChildren([]);
      setChildrenLoadError(true);
      setSelectedChild("");
      setSelectedTab("");

      const ax = error as {
        response?: { status?: number; data?: { error?: string } };
      };
      const errMsg = ax.response?.data?.error ?? "";
      if (
        ax.response?.status === 403 &&
        errMsg.includes("Kindergarten users are not allowed")
      ) {
        void (async () => {
          try {
            setIsLoading(true);
            setHasSearched(true);
            setLocationSearchMode(true);
            const csrfToken = await ensureCSRFToken();
            const res = await axios.get(
              `${API_BASE_URL}/api/search/my-kindergarten/`,
              {
                withCredentials: true,
                headers: {
                  "Content-Type": "application/json",
                  "X-CSRFToken": csrfToken,
                },
              }
            );
            const { latitude, longitude, uuid } = res.data as {
              latitude: string | null;
              longitude: string | null;
              uuid: string;
            };
            if (latitude && longitude) {
              setOwnDaycareUuid(uuid);
              const searchResponse = await searchDaycaresAPI(
                null,
                null,
                false,
                latitude,
                longitude,
                50,
                1
              );
              setLocationSearchCoords({
                lat: latitude,
                lon: longitude,
                radiusKm: 50,
                nameQuery: null,
              });
              setSearchResults(searchResponse.results);
              setSearchHasNext(searchResponse.has_next ?? false);
              setSearchListPage(1);
            }
          } catch (bootErr) {
            console.error("Provider map bootstrap failed:", bootErr);
          } finally {
            setIsLoading(false);
          }
        })();
      }
    } finally {
      setChildrenLoading(false);
    }
  };

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    setSearchResults([]);
    setSearchHasNext(false);
    setSearchListPage(1);
  }, []);

  useEffect(() => {
    // Reset search state when selected child changes, but not in location search mode
    if (!locationSearchMode) {
      setHasSearched(false);
      setSearchResults([]);
      setSearchHasNext(false);
      setSearchListPage(1);
    }
  }, [selectedChild, locationSearchMode]);

  useEffect(() => {
    const updateContainerHeight = () => {
      const screenWidth = window.innerWidth;
      const contentContainer = document.getElementById("content-container");

      if (contentContainer) {
        if (screenWidth >= 1280) {
          const headerHeight = 84.9062;
          contentContainer.style.height = `calc(100vh - ${headerHeight}px)`;
          document.body.style.overflow = "hidden";
        } else {
          contentContainer.style.height = "auto";
          document.body.style.overflow = "auto";
        }
      }
    };

    updateContainerHeight();
    window.addEventListener("resize", updateContainerHeight);

    return () => {
      window.removeEventListener("resize", updateContainerHeight);
    };
  }, []);

  useEffect(() => {
    const datePickers =
      document.querySelectorAll<HTMLInputElement>(".datePicker");
    const today = new Date().toISOString().split("T")[0];

    datePickers.forEach((datePicker) => {
      datePicker.value = today;
    });
  }, []);

  useEffect(() => {
    const loadScript = (src: string, async: boolean = true) => {
      return new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = async;
        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
      });
    };

    const loadScripts = async () => {
      try {
        const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        const mapsUrl = googleMapsApiKey 
          ? `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=geometry,places`
          : "https://maps.googleapis.com/maps/api/js?libraries=geometry,places";
        
        await loadScript(mapsUrl);
        await loadScript("https://code.jquery.com/jquery-3.6.0.min.js");
        await loadScript(
          "https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js"
        );
        setScriptsLoaded(true);
      } catch (error) {
        console.error("Error loading scripts:", error);
      }
    };

    loadScripts();
  }, []);

  useEffect(() => {
    if (scriptsLoaded && window.google && window.google.maps) {
      const mapElement = document.getElementById("map");
      if (mapElement) {
        const defaultLocation = { lat: 40.6782, lng: -73.9442 };

        try {
          const map = new window.google.maps.Map(mapElement, {
            zoom: 12,
            center: defaultLocation,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            styles: [
              {
                featureType: "poi",
                elementType: "labels.icon",
                stylers: [{ visibility: "off" }],
              },
            ],
          });

          setMapInstance(map);
        } catch (error) {
          console.error("Error initializing map:", error);
        }
      } else {
        console.error("Map element not found");
      }
    } else {
      // map element not yet available
    }
  }, [scriptsLoaded]);

  useEffect(() => {
    if (mapInstance) {
      updateMapMarkers(searchResults, ownDaycareUuid);
    }
  }, [searchResults, mapInstance, ownDaycareUuid]);

  useEffect(() => {
    if (!isAuthorized) return;
    let cleanup: (() => void) | undefined;
    const frame = requestAnimationFrame(() => {
      cleanup = pinBackButton();
    });
    return () => {
      cancelAnimationFrame(frame);
      cleanup?.();
    };
  }, [isAuthorized]);

  return (
    <main className="content">
      <div
        className="container-fluid"
        id="content-container"
        style={{ padding: "1rem" }}
      >
        <div className="row h-100 container-width">
          <div className="col-xl-6 d-flex flex-column h-100 left-side">
            <div className="flex-grow-1 overflow-hidden d-flex flex-column">
              <div className="tabs d-flex flex-column flex-grow-1 overflow-hidden">
                <div className="tab-contents d-flex flex-column flex-sm-row flex-grow-1 overflow-hidden border py-2 rounded-1">
                  <div
                    id="filter-tab"
                    className="tab-content flex-grow-1 overflow-auto pt-0 p-2 rounded-1"
                  >
                    {isAuthorized && (
                      <a
                        id="backButton"
                        href="/parent_dashboard"
                        className="btn btn-primary back-to-dashboard-btn mb-3 w-100"
                      >
                        <i className="bi bi-arrow-left-circle me-2"></i>
                        Back to dashboard
                      </a>
                    )}
                    <div className="d-flex flex-column align-items-start mb-3 w-100 position-relative">
                      <input
                        type="text"
                        id="searchInput"
                        className="form-control ps-5"
                        placeholder="Search by Day care..."
                        value={searchQuery}
                        onChange={handleSearchInput}
                        onKeyPress={handleSearchKeyPress}
                        disabled={isLoading}
                      />
                      <i
                        id="searchIcon"
                        className={`bi ${
                          isLoading ? "bi-arrow-clockwise" : "bi-search"
                        } position-absolute top-50 start-0 translate-middle-y ms-3`}
                        style={{ cursor: isLoading ? "default" : "pointer" }}
                        onClick={!isLoading ? handleSearchIconClick : undefined}
                      ></i>
                      {isLoading && (
                        <div className="position-absolute top-50 start-0 translate-middle-y ms-3">
                          <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="accordion" id="accordionExample">
                      {/* Child Select */}
                      {!childrenLoadError && (
                        <div className="accordion-item">
                        <h2 className="accordion-header" id="headingSeven">
                          <button
                            className="accordion-button"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseSeven"
                            aria-expanded="true"
                            aria-controls="collapseSeven"
                          >
                            Child Select
                          </button>
                        </h2>
                        <div
                          id="collapseSeven"
                          className="accordion-collapse collapse show"
                          aria-labelledby="headingSeven"
                        >
                          <div className="accordion-body p-3">
                            <div
                              className="position-relative"
                              id="toggleChildrenListBtn"
                            >
                              <div
                                className="children-checkboxes"
                                id="childrenList"
                              >
                                {availableChildren.map((child) => (
                                  <label
                                    key={child.id}
                                    className="d-flex align-items-center gap-2 mt-2"
                                  >
                                    <input
                                      type="radio"
                                      name="selectedChild"
                                      id={`child${child.id}`}
                                      checked={selectedChild === child.id.toString()}
                                      onChange={() => handleChildSelection(child.id.toString())}
                                      className="child-radio filter-radio"
                                    />
                                    {child.name}
                                  </label>
                                ))}
                                {availableChildren.length === 0 && (
                                  <div className="text-muted small">
                                    Loading children...
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        </div>
                      )}

                      {/* Availability */}
                      {!childrenLoadError && (
                        <div className="accordion-item">
                        <h2 className="accordion-header" id="headingThree">
                          <button
                            className="accordion-button"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseThree"
                            aria-expanded="true"
                            aria-controls="collapseThree"
                          >
                            Availability
                          </button>
                        </h2>
                        <div
                          id="collapseThree"
                          className="accordion-collapse collapse show"
                          aria-labelledby="headingThree"
                        >
                          <div className="accordion-body p-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="placeOptions"
                                id="allPlaces"
                                value="all"
                                checked={availabilityFilter === "all"}
                                onChange={(e) =>
                                  handleAvailabilityFilterChange(e.target.value)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="allPlaces"
                              >
                                All places
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="placeOptions"
                                id="availablePlaces"
                                value="available"
                                checked={availabilityFilter === "available"}
                                onChange={(e) =>
                                  handleAvailabilityFilterChange(e.target.value)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="availablePlaces"
                              >
                                Available places
                              </label>
                            </div>
                          </div>
                        </div>
                        </div>
                      )}
                    </div>

                    <button
                      id="saveButton"
                      className="btn btn-outline-primary mt-3 p-2 flex-fill w-100"
                      onClick={() => void performSearch()}
                      disabled={
                      childrenLoading ||
                      (!childrenLoadError &&
                        selectedChild === "" &&
                        !locationSearchMode) ||
                      isLoading
                      }
                    >
                      {isLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          ></span>
                          Searching...
                        </>
                      ) : (
                        "Show Results"
                      )}
                    </button>
                    <div className="btn-wrapper d-flex gap-2 justify-content-md-between w-100">
                      <i id="resetButton"></i>
                    </div>
                  </div>

                  <div
                    id="list-tab"
                    className="tab-content flex-grow-1 overflow-auto pt-2 pt-sm-0 p-2 rounded-1"
                  >
                    {/* Children Loading State */}
                    {childrenLoading && (
                      <div
                        className="d-flex flex-column justify-content-center align-items-center"
                        style={{ minHeight: "200px" }}
                      >
                        <div
                          className="spinner-border text-primary mb-3"
                          role="status"
                          style={{ width: "3rem", height: "3rem" }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <h6 className="text-muted mb-2">
                          Loading children...
                        </h6>
                        <small className="text-muted">
                          Please wait while we load your children's information
                        </small>
                      </div>
                    )}

                    {/* No Children Available */}
                    {!childrenLoading && availableChildren.length === 0 && !childrenLoadError && (
                      <div className="alert alert-warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No children found. Please add children to your account first.
                      </div>
                    )}

                    {/* No Selection Message */}
                    {!childrenLoading && 
                     availableChildren.length > 0 && 
                     selectedChild === "" && (
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        Please select a child to view daycare options.
                      </div>
                    )}

                    {/* Fallback for when children are loaded but none selected */}
                    {!childrenLoading && 
                     availableChildren.length > 0 && 
                     selectedChild === "" && 
                     selectedTab === "" && (
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        Please select at least one child to search for daycares.
                      </div>
                    )}

                    {/* Loading State */}
                    {!childrenLoading && 
                     (selectedChild !== "" || childrenLoadError) && 
                     isLoading && (
                      <div
                        className="d-flex flex-column justify-content-center align-items-center"
                        style={{ minHeight: "200px" }}
                      >
                        <div
                          className="spinner-border text-primary mb-3"
                          role="status"
                          style={{ width: "3rem", height: "3rem" }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <h6 className="text-muted mb-2">
                          Searching daycares...
                        </h6>
                        <small className="text-muted">
                          Please wait while we find the best matches for you
                        </small>
                      </div>
                    )}

                    {/* No Results State */}
                    {!childrenLoading &&
                     !isLoading &&
                     hasSearched &&
                     searchResults.length === 0 &&
                     (selectedChild !== "" || childrenLoadError || locationSearchMode) && (
                        <div className="alert alert-info">
                          <i className="bi bi-info-circle me-2"></i>
                          No daycares found matching your criteria.
                        </div>
                      )}

                    {/* Initial State - No Search Performed (not in location mode) */}
                    {!childrenLoading &&
                     !isLoading &&
                     !hasSearched &&
                     !locationSearchMode &&
                     (selectedChild !== "" || childrenLoadError) && (
                        <div className="alert alert-info">
                          <i className="bi bi-search me-2"></i>
                          Click "Show Results" to search for daycares.
                        </div>
                      )}

                    {/* Dynamic Tab Lists for Each Child or Global Results when children failed to load */}
                    {(!childrenLoading || locationSearchMode) && (
                      (childrenLoadError || locationSearchMode) ? (
                        <div
                          className="tab-child"
                          style={{ display: locationSearchMode ? "block" : undefined }}
                        >
                          {!isLoading && (hasSearched || locationSearchMode) &&
                            searchResults.map((daycare) => (
                              <DaycareCard key={daycare.id} daycare={daycare} />
                            ))}
                        </div>
                        ) : (
                        selectedChild !== "" &&
                        availableChildren.map(
                          (child) =>
                            selectedChild === child.id.toString() && (
                              <div
                                key={child.id}
                                id={`child${child.id}-tab`}
                                className="tab-child"
                                style={{
                                  display:
                                    selectedTab === child.id.toString()
                                      ? "block"
                                      : "none",
                                }}
                              >
                                {!isLoading &&
                                 hasSearched &&
                                 searchResults.map((daycare) => (
                                    <DaycareCard
                                      key={`child${child.id}-${daycare.id}`}
                                      daycare={daycare}
                                    />
                                  ))}
                              </div>
                            )
                        )
                      )
                    )}

                    {searchHasNext &&
                      (hasSearched || locationSearchMode) &&
                      !isLoading && (
                        <div className="d-flex justify-content-center py-3">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            disabled={isLoadingMore}
                            onClick={() => void loadMoreSearchResults()}
                          >
                            {isLoadingMore ? "Loading…" : "Load more"}
                          </button>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6 d-flex flex-column h-100 pt-3 p-lg-0 right-side">
            <div
              id="map"
              className="flex-grow-1 w-100 rounded-1"
              style={{
                minHeight: "400px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Toast Header Button */}
      <div className="toast-container position-fixed top-0 end-0 p-3">
        <div
          id="toastMessageHeader"
          className="toast position-relative"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div className="toast-body d-flex m-2">
            <i className="bi bi-info-circle text-success pe-2"></i>
            <span>Functionality is not available</span>
          </div>
        </div>
      </div>

      {/* Toast for showing "Application Sent" message */}
      <div className="toast-container position-fixed top-0 end-0 p-3">
        <div
          id="toastMessage"
          className="toast position-relative"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div className="toast-body d-flex m-2">
            <i className="bi bi-check-circle text-success pe-2"></i>
            <span id="toastMessageText">
              You have successfully submitted an application to join the Waiting
              List.
            </span>
          </div>
        </div>
      </div>

      {/* Modal Send */}
      <div
        className="modal-waiting modal fade"
        id="modalTabSend"
        tabIndex={-1}
        aria-labelledby="modalTabSendLabel"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header justify-content-center border-bottom-0">
              <h5 className="modal-title text-center">Send Application</h5>
              <button
                type="button"
                className="btn-close m-0 position-absolute"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmittingApplication}
              ></button>
            </div>
            <div className="modal-body pt-0">
              <div className="bg-secondary p-3 rounded mb-4 mt-1">
                <h6 className="fw-semibold pb-2" id="modalTabSendLabel">
                  Selected program
                </h6>

                <div className="d-grid d-sm-flex flex-wrap gap-3 justify-content-between">
                  <div className="d-flex flex-column">
                    <small className="text-muted pb-1">Program</small>
                    <small className="fw-semibold" id="modalProgramSend">
                      ---
                    </small>
                  </div>
                  <div className="d-flex align-items-end justify-content-start justify-content-sm-center">
                    <button className="btn btn-success btn-modal-sent rounded-0">
                      6 openings
                    </button>
                  </div>
                  <div className="d-flex flex-column">
                    <small className="text-muted pb-1">Price</small>
                    <small className="fw-semibold" id="modalPriceSend">
                      ---
                    </small>
                  </div>
                </div>
              </div>

                  <div className="form-container">
                <h6 className="fw-semibold pb-2">Child details</h6>
                <form id="sendReviewForm">
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <div className="flex-fill position-relative">
                      {childrenLoadError ? (
                        <div className="alert alert-warning small mb-0">
                          Child list unavailable. Please try again later.
                        </div>
                      ) : availableChildren.length === 0 ? (
                        <div className="alert alert-warning small mb-0">
                          No children found. Please add children to your account first.
                        </div>
                      ) : (
                        <>
                          <label htmlFor="childSelect" className="form-label small">
                            Select Child
                          </label>
                          <select
                            id="childSelect"
                            className="form-select"
                            value={selectedChildForApplication}
                            onChange={(e) =>
                              setSelectedChildForApplication(e.target.value)
                            }
                            required
                            disabled={isSubmittingApplication}
                          >
                            <option value="">Select a child...</option>
                            {availableChildren.map((child) => (
                              <option key={child.id} value={child.id.toString()}>
                                {child.gender === "m" ? "♂" : "♀"} {child.name} {child.date_of_birth}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
              <div className="modal-footer border-top-0">
               <button
                 type="button"
                 className="btn btn-primary"
                 disabled={
                   isSubmittingApplication || childrenLoadError || availableChildren.length === 0 || selectedChildForApplication === ""
                 }
                 onClick={() => handleApplicationSubmit("sent")}
                 id="sendApplicationBtnSent"
               >
                 Send Application
               </button>
             </div>
          </div>
        </div>
      </div>

      {/* Modal Waiting List */}
      <div
        className="modal modal-waiting fade"
        id="modalWaiting"
        tabIndex={-1}
        aria-labelledby="modalWaitingLabel"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header justify-content-center border-bottom-0">
              <h5 className="modal-title text-center" id="modalWaitingTitle">Join a waiting list</h5>
              <button
                type="button"
                className="btn-close m-0 position-absolute"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmittingApplication}
              ></button>
            </div>
            <div className="modal-body pt-0">
              <div className="bg-secondary p-3 rounded mb-4 mt-1">
                <h6 className="fw-semibold pb-2" id="modalWaitingLabel">
                  Selected program
                </h6>

                <div className="d-grid d-sm-flex flex-wrap gap-3 justify-content-between">
                  <div className="d-flex flex-column">
                    <small className="text-muted pb-1">Program</small>
                    <small className="fw-semibold" id="modalProgram">
                      ---
                    </small>
                  </div>
                  <div className="d-flex align-items-end justify-content-start justify-content-sm-center">
                    <button className="btn btn-info rounded-0" id="modalOpeningsWaiting">
                      Waiting List
                    </button>
                  </div>
                  <div className="d-flex flex-column">
                    <small className="text-muted pb-1">Price</small>
                    <small className="fw-semibold" id="modalPrice">
                      ---
                    </small>
                  </div>
                </div>
              </div>

                <div className="form-container">
                <h6 className="fw-semibold pb-2">Child details</h6>
                <form id="waitingReviewForm">
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <div className="flex-fill position-relative">
                      {childrenLoadError ? (
                        <div className="alert alert-warning small mb-0">
                          Child list unavailable. Please try again later.
                        </div>
                      ) : availableChildren.length === 0 ? (
                        <div className="alert alert-warning small mb-0">
                          No children found. Please add children to your account first.
                        </div>
                      ) : (
                        <>
                          <label htmlFor="childSelectWaiting" className="form-label small">
                            Select Child
                          </label>
                          <select
                            id="childSelectWaiting"
                            className="form-select"
                            value={selectedChildForApplication}
                            onChange={(e) =>
                              setSelectedChildForApplication(e.target.value)
                            }
                            required
                            disabled={isSubmittingApplication}
                          >
                            <option value="">Select a child...</option>
                            {availableChildren.map((child) => (
                              <option key={child.id} value={child.id.toString()}>
                                {child.gender === "m" ? "♂" : "♀"} {child.name} {child.date_of_birth}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
              <div className="modal-footer border-top-0 pt-1">
               <button type="button" className="btn btn-outline-primary" data-bs-dismiss="modal" id="revokeApplicationBtnWaiting">Revoke</button>
               <button
                 type="button"
                 className="btn btn-primary"
                 disabled={
                   isSubmittingApplication || childrenLoadError || availableChildren.length === 0 || selectedChildForApplication === ""
                 }
                 onClick={() => handleApplicationSubmit("waiting_list")}
                 id="sendApplicationBtnWaiting"
                 data-message="You have successfully submitted an application to join the Waiting List."
               >
                 Join a waiting list
               </button>
             </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default HomePageContent;